import { HABIT_COLORS } from '@td/shared';

/**
 * The colour scheme, swappable at runtime.
 *
 * Every colour in the app is a Tailwind class — `bg-cream`, `text-espresso`,
 * `border-sand`. Those now resolve to CSS variables rather than fixed hexes,
 * so changing a theme means writing fourteen variables and the whole
 * interface follows. Nothing has to be re-styled per theme.
 *
 * The variables hold channel triplets ("247 243 237") rather than hex, which
 * is what lets Tailwind's opacity modifiers keep working: `bg-rose/15`
 * compiles to `rgb(var(--c-rose) / 0.15)`.
 */

/**
 * The fourteen roles.
 *
 * The names are the original Boho colours, kept because renaming them would
 * mean touching every class in the app for no gain. Read them as roles: cream
 * is the page, espresso is the text, terracotta is the thing you press.
 */
export interface Palette {
  /** The page itself. Always the lightest. */
  cream: string;
  /** Raised surfaces — the sidebar, a header row. */
  linen: string;
  /** Borders and rules. */
  sand: string;
  /** Stronger borders, quiet marks. */
  clay: string;
  /** The primary accent: buttons, links, the thing to press. */
  terracotta: string;
  /** The primary pressed a shade harder, and destructive actions. */
  rust: string;
  /** Light positive. */
  sage: string;
  /** Dark positive — done, met, on track. */
  olive: string;
  /** Soft warning, gentle emphasis. */
  rose: string;
  /** A second accent, for variety across sections. */
  mauve: string;
  /** Attention: unsaved, due, warning. */
  ochre: string;
  /** Body text. Always the darkest. */
  espresso: string;
  /** Secondary text. */
  walnut: string;
  /** Quiet text — captions, placeholders. */
  stone: string;
}

export interface Theme {
  id: string;
  name: string;
  /** One line, so the picker says what it is rather than only showing dots. */
  blurb: string;
  palette: Palette;
}

export const THEMES: Theme[] = [
  {
    id: 'boho',
    name: 'Boho',
    blurb: 'Warm terracotta and sage. The one this was built in.',
    palette: {
      cream: '#F7F3ED',
      linen: '#EFE8DE',
      sand: '#DCCDBB',
      clay: '#C4A287',
      terracotta: '#B07C63',
      rust: '#9A6045',
      sage: '#A3B18A',
      olive: '#7C8A5F',
      rose: '#C08B8B',
      mauve: '#9C7A93',
      ochre: '#D9A85C',
      espresso: '#4A3F35',
      walnut: '#6B5D52',
      // Darkened from #9C9086: captions sat at 2.8:1 against cream, which is
      // below anything readable at twelve pixels. Still the same grey.
      stone: '#8B8074',
    },
  },
  {
    id: 'earthy',
    name: 'Earthy and natural',
    blurb: 'Moss, bark and clay. Like a walk in October.',
    palette: {
      cream: '#F6F4EE',
      linen: '#ECE8DD',
      sand: '#D6CDBA',
      clay: '#B5A483',
      terracotta: '#8A7A55',
      rust: '#6E5F3D',
      sage: '#9FB08C',
      olive: '#6B7F55',
      rose: '#B79B84',
      mauve: '#8B8471',
      ochre: '#C99A44',
      espresso: '#3D3A2E',
      walnut: '#5D5847',
      stone: '#918C7A',
    },
  },
  {
    id: 'minimal',
    name: 'Minimalist neutral',
    blurb: 'Greys and off-whites. Nothing shouts.',
    palette: {
      cream: '#FAFAF9',
      linen: '#F2F2F0',
      sand: '#DEDEDB',
      clay: '#BFBFBB',
      terracotta: '#7A7A75',
      rust: '#5C5C57',
      sage: '#B9C0B6',
      olive: '#778373',
      rose: '#C3B5B0',
      mauve: '#A7A2A8',
      ochre: '#C9B48A',
      espresso: '#2E2E2C',
      walnut: '#57574F',
      stone: '#8C8C86',
    },
  },
  {
    id: 'moody',
    name: 'Moody',
    blurb: 'Dusky slate and plum. Best late in the day.',
    palette: {
      cream: '#EDEBEF',
      linen: '#E1DEE4',
      sand: '#C5C0CC',
      clay: '#9E97A8',
      terracotta: '#6B5F7D',
      rust: '#4E4460',
      sage: '#8E9AA0',
      olive: '#5A6B70',
      rose: '#A6839A',
      mauve: '#7C6A8C',
      ochre: '#B08D6B',
      espresso: '#2A2532',
      walnut: '#463F52',
      stone: '#7E7789',
    },
  },
  {
    id: 'jewel',
    name: 'Jewel tone',
    blurb: 'Emerald, sapphire and garnet on ivory.',
    palette: {
      cream: '#F7F4EF',
      linen: '#EDE7DE',
      sand: '#D5C9B8',
      clay: '#B09A78',
      terracotta: '#1F6F5C',
      rust: '#8C2F39',
      sage: '#6FA898',
      olive: '#14574A',
      rose: '#A63A4A',
      mauve: '#5B3E7A',
      ochre: '#C99A2E',
      espresso: '#22332F',
      walnut: '#3F5C54',
      stone: '#7C8A85',
    },
  },
  {
    id: 'pastel',
    name: 'Pastel',
    blurb: 'Soft sherbet colours. Kind on the eye.',
    palette: {
      cream: '#FDFAF7',
      linen: '#F8F1F3',
      sand: '#EBDCE2',
      clay: '#D9BFC9',
      // Deepened from #C48FA5, which put cream button text at 2.6:1. A pastel
      // theme still needs one colour dark enough to press.
      terracotta: '#A6647E',
      rust: '#894F68',
      sage: '#B9D8C8',
      olive: '#7FAE97',
      rose: '#EFB8C0',
      mauve: '#B7A6D4',
      ochre: '#F2CE8E',
      espresso: '#4B3F49',
      walnut: '#6E6070',
      stone: '#8A7C8A',
    },
  },
  {
    id: 'dreamy',
    name: 'Dreamy',
    blurb: 'Lilac and sky, like an evening sky.',
    palette: {
      cream: '#F8F7FD',
      linen: '#EFEDFA',
      sand: '#DBD7F0',
      clay: '#BAB4E0',
      terracotta: '#8B84CE',
      rust: '#6A61B0',
      sage: '#A8D2E8',
      olive: '#5E93B5',
      rose: '#D5A8DE',
      mauve: '#A98FD1',
      ochre: '#F0BFA0',
      espresso: '#3A3552',
      walnut: '#5A5378',
      stone: '#8E88A8',
    },
  },
  {
    id: 'retro',
    name: 'Retro',
    blurb: 'Mustard, avocado and orange. Nineteen seventy-two.',
    palette: {
      cream: '#FBF3E4',
      linen: '#F5E8D2',
      sand: '#E4CFA8',
      clay: '#C9A96A',
      terracotta: '#D2703A',
      rust: '#A8481F',
      sage: '#B3BE6A',
      olive: '#6E7A2A',
      rose: '#DB9A6A',
      mauve: '#9C6A52',
      ochre: '#E8A317',
      espresso: '#3C2E1E',
      walnut: '#5F4A2F',
      stone: '#94805F',
    },
  },
  {
    id: 'vintage',
    name: 'Vintage',
    blurb: 'Faded paper and old ink. Quiet and worn.',
    palette: {
      cream: '#F4EEE2',
      linen: '#EAE1D0',
      sand: '#D3C5AC',
      clay: '#B3A183',
      terracotta: '#8E6E52',
      rust: '#6F4F38',
      sage: '#A8AC90',
      olive: '#75795C',
      rose: '#B58C7C',
      mauve: '#8A7668',
      ochre: '#C0964F',
      espresso: '#3B342A',
      walnut: '#5C5245',
      stone: '#8F8676',
    },
  },
  {
    id: 'vivid',
    name: 'Vivid',
    blurb: 'Bright and unmissable. For a room that needs waking up.',
    palette: {
      cream: '#FFFDF7',
      linen: '#FFF4E3',
      sand: '#FBDCB4',
      clay: '#F5B461',
      terracotta: '#E8562F',
      rust: '#BE2D14',
      sage: '#5FC9A0',
      olive: '#0E8F6B',
      rose: '#F2607A',
      mauve: '#7A4FCF',
      ochre: '#F5A800',
      espresso: '#2B1E16',
      walnut: '#5A4034',
      stone: '#96837A',
    },
  },
];

export const DEFAULT_THEME = 'boho';

export function themeById(id: string | null | undefined): Theme {
  return THEMES.find((t) => t.id === id) ?? THEMES[0]!;
}

/**
 * A CSS colour that always reflects the theme in force.
 *
 * The variables are written to the document, so anything using this is
 * correct the instant the theme changes — no render ordering to get wrong,
 * and no stale palette read during a render that happens before the effect.
 */
export function themeVar(role: keyof Palette): string {
  return `rgb(var(--c-${role}))`;
}

/**
 * Which palette role each habit colour corresponds to.
 *
 * Habits store a hex, chosen from the shared list when they were created. The
 * two lists line up one for one, so the stored colour can be read back as a
 * role and drawn in whatever theme is current — rather than a habit created
 * under Boho staying terracotta forever in a Vivid dashboard.
 */
const HABIT_ROLES: (keyof Palette)[] = ['terracotta', 'sage', 'mauve', 'ochre', 'rose', 'clay'];

export function habitColor(stored: string): string {
  const index = HABIT_COLORS.indexOf(stored as (typeof HABIT_COLORS)[number]);
  // A colour we did not assign is left exactly as it is.
  return index === -1 ? stored : themeVar(HABIT_ROLES[index % HABIT_ROLES.length]!);
}

/* ------------------------------------------------------------- utilities */

/** "#B07C63" to "176 124 99", which is what Tailwind's alpha syntax wants. */
export function channels(hex: string): string {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return `${r} ${g} ${b}`;
}

/** Blends two hex colours. `amount` is how much of the first to keep. */
export function mix(hex: string, toward: string, amount: number): string {
  const parse = (h: string) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
  const [r1, g1, b1] = parse(hex);
  const [r2, g2, b2] = parse(toward);

  const blend = (a: number, b: number) => Math.round(a * amount + b * (1 - amount));

  return (
    '#' +
    [blend(r1!, r2!), blend(g1!, g2!), blend(b1!, b2!)]
      .map((v) => v.toString(16).padStart(2, '0'))
      .join('')
  );
}

/** Relative luminance, per WCAG. */
function luminance(hex: string): number {
  const channelsOf = [1, 3, 5]
    .map((i) => parseInt(hex.slice(i, i + 2), 16) / 255)
    .map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));

  return 0.2126 * channelsOf[0]! + 0.7152 * channelsOf[1]! + 0.0722 * channelsOf[2]!;
}

/** The WCAG contrast ratio between two colours, from 1 to 21. */
export function contrast(a: string, b: string): number {
  const [lighter, darker] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (lighter! + 0.05) / (darker! + 0.05);
}

/** A colour plus an alpha channel, as an 8-digit hex. */
export function alpha(hex: string, amount: number): string {
  return (
    hex +
    Math.round(amount * 255)
      .toString(16)
      .padStart(2, '0')
  );
}

/*
 * The palette in force, held at module level.
 *
 * Read from plain functions all over the app — the sidebar's colours, the
 * dashboard's card accents, the background list — and threading a context
 * through all of them would touch far more code than it would clarify. It is
 * written once, by the appearance provider, on a change that re-renders
 * everything under it anyway.
 */
let active: Palette = THEMES[0]!.palette;

export function setPalette(palette: Palette): void {
  active = palette;
}

export function palette(): Palette {
  return active;
}

export interface SectionColor {
  /** The lightest wash: a sidebar row at rest, the fill behind a card. */
  wash: string;
  /** The same colour under the cursor. */
  tint: string;
  /** Borders, and decoration that carries no words. */
  base: string;
  /**
   * Words, on a washed row.
   *
   * `base` is the theme's raw accent, and a raw accent is not a text colour —
   * on the brighter palettes several of them came out as pale words on a pale
   * row, which is the ghosting on the sidebar that made this necessary. This
   * is the same hue darkened until it genuinely reads.
   */
  label: string;
  /** Filled — the page you are on, a pressed control. */
  deep: string;
  /** `deep` a shade further down, for hovering a filled control. */
  press: string;
  /**
   * A whole bar of the colour, with light writing on it.
   *
   * Darker than `deep`, and for a specific reason: `deep` is the lightest
   * shade full-strength cream reads on, so a *quieter* second line on it —
   * cream at 82%, which is what tells a heading from its date — lands around
   * 3.7:1. This is darkened until that quieter line reaches 4.5:1, which
   * means both lines are readable rather than only the loud one.
   */
  fill: string;
}

/**
 * A section's four shades, derived rather than listed.
 *
 * Ten themes times nine sections would be three hundred and sixty hand-picked
 * colours, and every new theme would mean picking thirty-six more. These come
 * out of the palette by rule, so a new theme is fourteen colours and nothing
 * else.
 */
/**
 * Comfortably readable at the size the sidebar uses.
 *
 * 4.5:1 is the WCAG AA figure for normal text. The old check here settled for
 * 3, which is the figure for *large* text — and a 14px sidebar row is not
 * large text. That one number is why ten themes were certified readable while
 * half their labels were ghosts.
 */
const READABLE = 4.5;

function shadesOf(hex: string, page: string, surface: string): SectionColor {
  /*
   * `deep` is darkened until cream text actually reads on it, rather than by
   * a fixed amount.
   *
   * A fixed ratio was tried and failed on the pale themes — Pastel's ochre
   * and Dreamy's sage came out around 2.8:1 behind white text on the active
   * sidebar row. Measuring instead of guessing means a new theme can be any
   * fourteen colours at all and this still holds.
   */
  let deep = hex;
  for (let keep = 0.8; keep >= 0.25; keep -= 0.05) {
    deep = mix(hex, '#000000', keep);
    if (contrast(deep, page) >= READABLE) break;
  }

  /*
   * And the resting row's text, darkened against what that row actually is.
   *
   * The background here is not the surface — it is the accent at 9% over the
   * surface, which is what `wash` paints. Measuring the text against the bare
   * surface would flatter it by exactly the amount the wash darkens the row,
   * and on the pale accents that is the difference between reading and
   * squinting.
   *
   * The hue is kept, so the sidebar still has a colour per section; only the
   * lightness moves, and only as far as it has to.
   */
  /*
   * Measured against the *hovered* row, at 20%, not the resting one at 9%.
   *
   * The darker of the two states is the one that decides: a label derived
   * against the resting row went pale again the moment the cursor landed on
   * it, which is precisely when somebody is reading it.
   */
  const resting = mix(hex, surface, 0.2);

  let label = hex;
  for (let keep = 1; keep >= 0.2; keep -= 0.05) {
    label = mix(hex, '#000000', keep);
    if (contrast(label, resting) >= READABLE) break;
  }

  /*
   * The quieter line is what decides how dark a filled bar has to be.
   *
   * Derived rather than picked, the same way everything else here is: an
   * opacity that looked right against one theme's terracotta was three
   * quarters of the way to invisible against another's sage.
   */
  let fill = deep;
  for (let keep = 1; keep >= 0.2; keep -= 0.05) {
    fill = mix(deep, '#000000', keep);
    if (contrast(mix(page, fill, 0.82), fill) >= READABLE) break;
  }

  return {
    wash: alpha(hex, 0.09),
    tint: alpha(hex, 0.2),
    base: hex,
    label,
    deep,
    fill,
    // Only downwards: `deep` is already the lightest shade cream reads on, so
    // any lighter hover state is a button that dims as you point at it.
    press: mix(deep, '#000000', 0.82),
  };
}

/** Which palette colour each section wears. */
const SECTION_ROLES: Record<string, keyof Palette> = {
  '/': 'terracotta',
  '/calendar': 'mauve',
  '/lesson-plans': 'olive',
  '/sub-plans': 'espresso',
  '/todos': 'ochre',
  '/roster': 'rust',
  '/seating': 'stone',
  '/jobs': 'ochre',
  '/photos': 'rose',
  '/documents': 'sage',
  '/data': 'sage',
  '/notes': 'clay',
  '/bookmarks': 'rose',
  '/settings': 'walnut',
};

/**
 * One palette colour, in the same four-and-three shades a section gets.
 *
 * For the header when she has picked a colour rather than letting each page
 * keep its own. Derived from whichever theme is in force, so a header set to
 * `mauve` follows the theme's mauve rather than freezing one theme's version
 * of it — and the readable shades are worked out the same way, so a chosen
 * colour is no more likely to be unreadable than a section's own.
 */
export function headerShades(role: keyof Palette): SectionColor {
  const p = active;
  return shadesOf(p[role], p.cream, p.linen);
}

export function sectionColors(): Record<string, SectionColor> {
  const p = active;

  return Object.fromEntries(
    /*
     * Measured against `linen`, not `cream`. The sidebar is the raised
     * surface — a row at rest sits on linen, and linen is the darker of the
     * two, so a label that reads here reads on a card as well.
     */
    Object.entries(SECTION_ROLES).map(([route, role]) => [
      route,
      shadesOf(p[role], p.cream, p.linen),
    ]),
  );
}
